import { Calendar, Clock, MapPin, Phone, Users, Video } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';

export function NextMeeting() {
  const [meetingType, setMeetingType] = useState('');
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);

  const meetingTypes = [
    {
      id: 'discovery',
      label: 'Discovery Call',
      icon: Phone,
      description: 'Initial needs assessment',
    },
    { id: 'demo', label: 'Product Demo', icon: Video, description: 'Platform walkthrough' },
    {
      id: 'technical',
      label: 'Technical Review',
      icon: Users,
      description: 'Deep dive with IT team',
    },
    {
      id: 'proposal',
      label: 'Proposal Review',
      icon: MapPin,
      description: 'Present formal proposal',
    },
    {
      id: 'contract',
      label: 'Contract Discussion',
      icon: Users,
      description: 'Terms and conditions',
    },
  ];

  const suggestedAttendees = [
    'Sales Representative',
    'Solution Engineer',
    'Technical Specialist',
    'Account Manager',
    'Implementation Manager',
    'Executive Sponsor',
  ];

  const toggleAttendee = (attendee: string) => {
    setSelectedAttendees((prev) =>
      prev.includes(attendee) ? prev.filter((a) => a !== attendee) : [...prev, attendee]
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Meeting Type & Purpose
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={meetingType} onValueChange={setMeetingType}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {meetingTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    type="button"
                    key={type.id}
                    className={`flex items-start space-x-3 p-4 border-2 transition-colors text-left ${
                      meetingType === type.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => setMeetingType(type.id)}
                  >
                    <RadioGroupItem value={type.id} />
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-sm text-muted-foreground">{type.description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Schedule Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meetingDate">Preferred Date</Label>
              <Input id="meetingDate" type="date" min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meetingTime">Preferred Time</Label>
              <Input id="meetingTime" type="time" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Duration</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="est">Eastern Time (EST)</SelectItem>
                  <SelectItem value="cst">Central Time (CST)</SelectItem>
                  <SelectItem value="mst">Mountain Time (MST)</SelectItem>
                  <SelectItem value="pst">Pacific Time (PST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Meeting Location/Platform</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="webex">Webex</SelectItem>
                  <SelectItem value="phone">Phone Call</SelectItem>
                  <SelectItem value="onsite">On-site Visit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Meeting Attendees
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-base">Our Team (SteerFi)</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
              {suggestedAttendees.map((attendee) => (
                <button
                  type="button"
                  key={attendee}
                  className={`flex items-center space-x-2 p-4 border-2 transition-colors cursor-pointer ${
                    selectedAttendees.includes(attendee)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleAttendee(attendee)}
                >
                  <input
                    type="checkbox"
                    checked={selectedAttendees.includes(attendee)}
                    onChange={() => toggleAttendee(attendee)}
                    className="rounded border-border"
                  />
                  <span className="text-sm font-medium">{attendee}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientAttendees">Client Attendees</Label>
            <Textarea
              id="clientAttendees"
              placeholder="List expected attendees from the client side..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Meeting Agenda & Objectives</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="objectives">Meeting Objectives</Label>
            <Textarea
              id="objectives"
              placeholder="What do we want to accomplish in this meeting?"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agenda">Detailed Agenda</Label>
            <Textarea id="agenda" placeholder="Outline the meeting agenda items..." rows={4} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="preparation">Pre-meeting Preparation</Label>
            <Textarea
              id="preparation"
              placeholder="What materials or information should be prepared beforehand?"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Follow-up Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="followUpActions">Planned Follow-up Actions</Label>
            <Textarea
              id="followUpActions"
              placeholder="What actions will we take after this meeting?"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nextMeetingDate">Next Meeting Target Date</Label>
              <Input
                id="nextMeetingDate"
                type="date"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner">Action Owner</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Assign owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales-rep">Sales Representative</SelectItem>
                  <SelectItem value="account-manager">Account Manager</SelectItem>
                  <SelectItem value="solution-engineer">Solution Engineer</SelectItem>
                  <SelectItem value="team">Team Collaboration</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-dashed border-2">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Calendar className="w-8 h-8 text-muted-foreground mb-2" />
          <p className="text-muted-foreground mb-4">Ready to schedule the meeting?</p>
          <div className="flex gap-2">
            <Button variant="outline">
              <Calendar className="w-4 h-4 mr-2" />
              Send Calendar Invite
            </Button>
            <Button>
              <Video className="w-4 h-4 mr-2" />
              Create Meeting Link
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
